<script setup lang="ts">
import type { SortableEvent } from "sortablejs";
import { computed, nextTick, ref, useTemplateRef } from "vue";
import { VueDraggableNext as VueDraggable } from "vue-draggable-next";

import { objectiveActions } from "@/action/objectiveActions";
import { objectiveMarkedAs } from "@/analytics/events/other";
import { trackEvent } from "@/analytics/track";
import MenuItemDelimiter from "@/components/menu/MenuItemDelimiter.vue";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { boardTypeToLevel } from "@/model/baseTypes";
import type { BoardWithObjectives } from "@/model/board";
import type { Objective, ObjectiveType } from "@/model/objective";
import { useUserStore } from "@/store/user";

import AddObjectiveButton from "./AddObjectiveButton.vue";
import AddObjectiveCard from "./AddObjectiveCard.vue";
import ObjectiveCard from "./ObjectiveCard.vue";

const props = defineProps<{
  board: BoardWithObjectives;
  noStatusDistribution?: boolean;
  group?: string;
}>();

const committedHeaderRef = useTemplateRef("committedHeader");
const uncommittedHeaderRef = useTemplateRef("uncommittedHeader");
const addCommittedObjectiveButtonRef = useTemplateRef("addCommittedObjectiveButton");
const addUncommittedObjectiveButtonRef = useTemplateRef("addUncommittedObjectiveButton");
const committedTriggerRef = useTemplateRef("committedTrigger");
const uncommittedTriggerRef = useTemplateRef("uncommittedTrigger");

const addCommittedObjectiveIsActive = ref(false);
const addUncommittedObjectiveIsActive = ref(false);
const isDragging = ref(false);
const committedIsExpanded = ref(true);
const uncommittedIsExpanded = ref(true);
const expandDescriptions = ref(false);

const objectives = computed(() => props.board.objectives);
const stretchObjectives = computed(() => props.board.stretchObjectives);
const showNoObjectives = computed(() => objectives.value.length === 0);
const showNoStretchObjectives = computed(() => stretchObjectives.value.length === 0);
const readonly = computed(() => !useUserStore().isAllowed("edit"));

function toggleGlobalDescriptions() {
  expandDescriptions.value = !expandDescriptions.value;
}

function addDragStateToCommittedHeader() {
  committedHeaderRef.value!.classList.add("drag-state");
}

function removeDragStateFromCommittedHeader() {
  committedHeaderRef.value!.classList.remove("drag-state");
}

function addDragStateToUncommittedHeader() {
  uncommittedHeaderRef.value!.classList.add("drag-state");
}

function removeDragStateFromUncommittedHeader() {
  uncommittedHeaderRef.value!.classList.remove("drag-state");
}

function hasDraggedToCommittedHeader(element: Element) {
  return element.classList?.contains("header-committed-group");
}

function hasDraggedToUncommittedHeader(element: Element) {
  return element.classList?.contains("header-uncommitted-group");
}

function onEndDragging(event: SortableEvent) {
  /**
   * updates the commitment state for the objective that was dragged
   */
  isDragging.value = false;
  const hasDraggedBetweenUncommittedObjectives = event.to.classList.contains("uncommitted-group");

  const wasStretch = !!event.from?.classList.contains("uncommitted-group");

  const isStretch =
    hasDraggedBetweenUncommittedObjectives || hasDraggedToUncommittedHeader(event.to);

  const hasDraggedToHeader =
    hasDraggedToCommittedHeader(event.to) || hasDraggedToUncommittedHeader(event.to);

  const index = hasDraggedToHeader ? (isStretch ? 0 : objectives.value.length) : event.newIndex;

  const objectiveId = event.item.dataset.id!;

  objectiveActions.move("modal", props.board.id, objectiveId, {
    stretch: isStretch,
    rank: index ? index : 0,
  });
  removeDragStateFromUncommittedHeader();
  removeDragStateFromCommittedHeader();

  if (wasStretch !== isStretch) {
    trackEvent(objectiveMarkedAs(objectiveId, !!isStretch, boardTypeToLevel(props.board.type)));
  }
}

function onMove(event: SortableEvent, _originalEvent: unknown) {
  if (hasDraggedToCommittedHeader(event.to) && !committedIsExpanded.value) {
    addDragStateToCommittedHeader();
    removeDragStateFromUncommittedHeader();
  } else if (hasDraggedToUncommittedHeader(event.to) && !uncommittedIsExpanded.value) {
    addDragStateToUncommittedHeader();
    removeDragStateFromCommittedHeader();
  } else {
    removeDragStateFromCommittedHeader();
    removeDragStateFromUncommittedHeader();
  }
}

function cancelAddCommitted() {
  addCommittedObjectiveIsActive.value = false;
  addCommittedObjectiveButtonRef.value?.focus();
}

function cancelAddUncommitted() {
  addUncommittedObjectiveIsActive.value = false;
  addUncommittedObjectiveButtonRef.value?.focus();
}

/**
 * Focus on the objective card after adding it (by id)
 */
async function focusOnObjective(objective: Objective) {
  await nextTick();
  const objectiveCard = document.getElementById(`objective-card-${objective.id}`);
  objectiveCard?.focus();
}

// Move focus to the section header (trigger) when an objective is removed
async function onObjectiveRemoved(type: ObjectiveType) {
  await nextTick(); // Wait for focus-trap to deactivate
  if (type === "committed") {
    committedTriggerRef.value?.focus();
  } else {
    uncommittedTriggerRef.value?.focus();
  }
}
</script>

<template>
  <div>
    <div ref="committedHeader" class="objectives-header header-committed-group">
      <h3>
        <button
          ref="committedTrigger"
          class="collapsable-area"
          data-testid="collapsable-area"
          :aria-expanded="committedIsExpanded"
          :aria-controls="`committed-group-${board.id}`"
          @click.stop="committedIsExpanded = !committedIsExpanded"
        >
          <SvgIcon v-if="committedIsExpanded" name="arrow/chevron-down" width="20" height="20" />
          <SvgIcon v-else name="arrow/chevron-right" width="20" height="20" />
          <div>
            <p class="objectives-header-title">
              {{ $t("objectives.committed") }}
            </p>
            <p class="objective-count">
              {{ $t("objectives.count", objectives.length) }}
            </p>
          </div>
        </button>
      </h3>
      <div class="objectives-actions">
        <BaseButton
          variant="ghost"
          color="grey"
          icon-before="general/description"
          @click="toggleGlobalDescriptions"
          >{{
            expandDescriptions
              ? $t("objectives.hideDescriptionButton")
              : $t("objectives.showDescriptionButton")
          }}
        </BaseButton>
        <template v-if="!readonly">
          <MenuItemDelimiter />
          <AddObjectiveButton
            ref="addCommittedObjectiveButton"
            class="objective-btn"
            @click="
              addCommittedObjectiveIsActive = true;
              committedIsExpanded = true;
            "
          />
        </template>
      </div>
    </div>
    <div
      v-if="committedIsExpanded"
      :id="`committed-group-${board.id}`"
      data-testid="committed-group-container"
      class="objectives-container"
    >
      <AddObjectiveCard
        v-if="addCommittedObjectiveIsActive"
        type="committed"
        :board
        @cancel="cancelAddCommitted"
        @added="focusOnObjective"
      />
      <VueDraggable
        :list="objectives"
        ghost-class="objective-drag-state"
        class="committed-group"
        :group="group || 'objectives'"
        :move="onMove"
        :disabled="readonly"
        animation="100"
        @start="isDragging = true"
        @end="onEndDragging"
      >
        <ObjectiveCard
          v-for="(objective, index) in objectives"
          :key="objective.id"
          :objective
          type="committed"
          :index="index + 1"
          :board
          :is-dragging
          :expand-descriptions
          :has-status-distribution="!noStatusDistribution"
          :can-move-up="index > 0"
          :can-move-down="index < objectives.length - 1"
          @removed="onObjectiveRemoved('committed')"
        />
      </VueDraggable>
      <div v-if="showNoObjectives" class="no-objective-placeholder">
        <p>{{ $t("objectives.none") }}</p>
      </div>
    </div>

    <div ref="uncommittedHeader" class="objectives-header header-uncommitted-group">
      <h3>
        <button
          ref="uncommittedTrigger"
          class="collapsable-area"
          data-testid="collapsable-area"
          :aria-expanded="uncommittedIsExpanded"
          :aria-controls="`uncommitted-group-${board.id}`"
          @click.stop="uncommittedIsExpanded = !uncommittedIsExpanded"
        >
          <SvgIcon v-if="uncommittedIsExpanded" name="arrow/chevron-down" width="20" height="20" />
          <SvgIcon v-else name="arrow/chevron-right" width="20" height="20" />
          <div>
            <p class="objectives-header-title">
              {{ $t("objectives.uncommitted") }}
            </p>
            <p class="objective-count">
              {{ $t("objectives.count", stretchObjectives.length) }}
            </p>
          </div>
        </button>
      </h3>
      <AddObjectiveButton
        v-if="!readonly"
        ref="addUncommittedObjectiveButton"
        class="objective-btn"
        @click="
          addUncommittedObjectiveIsActive = true;
          uncommittedIsExpanded = true;
        "
      />
    </div>
    <div
      v-if="uncommittedIsExpanded"
      :id="`uncommitted-group-${board.id}`"
      data-testid="uncommitted-group-container"
      class="objectives-container"
    >
      <AddObjectiveCard
        v-if="addUncommittedObjectiveIsActive"
        type="uncommitted"
        :board
        @cancel="cancelAddUncommitted"
        @added="focusOnObjective"
      />

      <VueDraggable
        :list="stretchObjectives"
        ghost-class="objective-drag-state"
        draggable=".objective-card"
        class="uncommitted-group"
        :group="group || 'objectives'"
        :move="onMove"
        :disabled="readonly"
        animation="100"
        @start="isDragging = true"
        @end="onEndDragging"
      >
        <ObjectiveCard
          v-for="(objective, index) in stretchObjectives"
          :key="objective.id"
          :objective
          type="uncommitted"
          :index="index + 1"
          :board
          :is-dragging
          :expand-descriptions
          :has-status-distribution="!noStatusDistribution"
          :can-move-up="index > 0"
          :can-move-down="index < stretchObjectives.length - 1"
          @removed="onObjectiveRemoved('uncommitted')"
        />
      </VueDraggable>
      <div v-if="showNoStretchObjectives" class="no-objective-placeholder">
        <p>{{ $t("objectives.none") }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/font";
@use "@/styles/variables";
@use "@/styles/variables/colors";
@use "@/styles/z-index";

.header-committed-group,
.header-uncommitted-group {
  position: sticky;
  top: 0;
  background-color: colors.$background-ui;
  z-index: z-index.$low;
}

.objectives-container {
  margin: 0 20px;
}

.objectives-header {
  margin: 40px 0 20px;
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-radius: 7px;
  vertical-align: center;

  .objectives-header-title {
    font-weight: font.$weight-extra-bold;
    font-size: font.$size-normal;
    margin: 0;
  }

  .objectives-actions {
    display: flex;
    align-items: center;
  }

  .collapsable-area {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 16px;
    outline: revert;
  }
}

.objective-drag-state {
  background-color: colors.$alpha-5;
  cursor: grabbing;
}

.drag-state {
  background-color: colors.$alpha-5;
  cursor: grabbing;

  & ~ .header-drag-state {
    display: none;
  }
}

.no-objective-placeholder {
  border: 1px solid colors.$border-subtle;
  padding: 1.2rem;
  margin-bottom: 1.2rem;
  border-radius: variables.$objective-card-border-radius;

  p {
    color: colors.$text-placeholder;
    font-size: font.$size-small;
    text-align: center;
  }
}

.objective-count {
  font-size: font.$size-small;
  margin: 4px 0 0;
  color: colors.$text-subtle;
}
</style>
